// src/mobile/MobileBottomNav.js
// Scroll-activated bottom navigation bar

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Layers, Monitor, BookOpen, Mail } from 'lucide-react';
import './styles/mobile-bottom-nav.css';

// Navigation items (static, outside component to avoid dependency issues)
const NAV_ITEMS = [
  { id: 'hero', label: 'Home', Icon: Home },
  { id: 'capabilities', label: 'Capabilities', Icon: Layers },
  { id: 'demos', label: 'Demos', Icon: Monitor },
  { id: 'resources', label: 'Resources', Icon: BookOpen, isRoute: true, href: '/resources' },
  { id: 'contact', label: 'Contact', Icon: Mail },
];

const MobileBottomNav = ({ alwaysVisible = false, activeItem = null }) => {
  const [isVisible, setIsVisible] = useState(alwaysVisible);
  const [activeSection, setActiveSection] = useState(activeItem || 'hero');
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll to show/hide nav and detect active section
  const handleScroll = useCallback(() => {
    // If alwaysVisible, skip scroll-based visibility
    if (alwaysVisible) return;

    const scrollY = window.scrollY;

    // Show nav after just 10px of scroll - very sensitive
    setIsVisible(scrollY > 10);

    // Detect active section based on scroll position
    const sections = NAV_ITEMS.map((item) => document.getElementById(item.id));
    const scrollPosition = scrollY + window.innerHeight / 2;

    for (let i = sections.length - 1; i >= 0; i--) {
      const section = sections[i];
      if (section) {
        const sectionTop = section.offsetTop;
        if (scrollPosition >= sectionTop) {
          setActiveSection(NAV_ITEMS[i].id);
          break;
        }
      }
    }
  }, [alwaysVisible]);

  useEffect(() => {
    // If always visible, set it immediately
    if (alwaysVisible) {
      setIsVisible(true);
      return;
    }

    // Initial check
    handleScroll();

    // Add scroll listener with passive flag for performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, alwaysVisible]);

  const handleNavClick = (e, item) => {
    e.preventDefault();
    if (item.isRoute) {
      // Navigate to route
      navigate(item.href);
    } else {
      // Check if we're on the home page
      if (location.pathname === '/') {
        // Scroll to section
        const section = document.getElementById(item.id);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to home with anchor
        navigate('/');
        // Increased timeout to allow page to fully render before scrolling
        setTimeout(() => {
          const section = document.getElementById(item.id);
          if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
          }
        }, 400);
      }
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.nav
          className="mobile-bottom-nav"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
          }}
        >
          <div className="mobile-bottom-nav__inner">
            {NAV_ITEMS.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  className={`mobile-bottom-nav__item ${isActive ? 'mobile-bottom-nav__item--active' : ''}`}
                  onClick={(e) => handleNavClick(e, item)}
                  aria-label={`Navigate to ${item.label}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {isActive && (
                    <motion.div
                      className="mobile-bottom-nav__indicator"
                      layoutId="nav-indicator"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <item.Icon
                    size={20}
                    strokeWidth={isActive ? 2 : 1.5}
                    className="mobile-bottom-nav__icon"
                  />
                  <span className="mobile-bottom-nav__label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};

export default MobileBottomNav;
